import Form from '@core/managers/Form';
import Field from '@core/managers/Field';
import { extractTargetString } from '@core/utils/common';

/**
 * Encapsulates in a given object, at a given path the provided value
 *
 * @example
 * ORIGINAL object
 *
 * encapsulateIn({maintain: 'spread_me'}, 'a.b.c','test')
 *
 * RESULT
 *
 * {
 *  maintain: 'spread_me',
 *    c: 'test
 *  }
 * }
 *
 * @param origin - The original object where the new value will be appended
 * @param path - The path at which the new value will be placed
 * @param value - The new value
 * @param form - The form instance to be used on extract string
 * @param field - The field instance to be used on extract string
 * @returns One new object with the new value at the provided path merged with the given object
 */
const encapsulateIn = (
  origin: Record<string, unknown>,
  path: string,
  value: string,
  form: Form,
  field: Field,
): Record<string, unknown> => {
  const getType = (v: string | number | boolean | Record<string, unknown>) => {
    if (typeof value === 'boolean' && typeof v === 'string') {
      return /true/.test(v);
    }

    return value.constructor(v);
  };
  const parts = path.split('.');
  if (parts.length === 1) {
    return {
      ...origin,
      [path]: getType(
        extractTargetString<
          string | number | boolean | Record<string, unknown>
        >(value, form.scope, field.scope),
      ),
    };
  }
  const getNewPart = (
    parts: string[],
    subObject: Record<string, unknown>,
  ): Record<string, unknown> => {
    const clonedParts = [...parts];
    clonedParts.splice(0, 1);
    const part =
      parts.length !== 1
        ? getNewPart(
            clonedParts,
            subObject ? (subObject[parts[0]] as Record<string, unknown>) : {},
          )
        : getType(
            extractTargetString<
              string | number | boolean | Record<string, unknown>
            >(value, form.scope, field.scope),
          );
    return {
      ...subObject,
      [parts[0]]: part,
    };
  };
  return getNewPart(parts, origin);
};

export { encapsulateIn };
